import { NgForOf, NgIf, NgClass } from '@angular/common';
import { Component, computed, OnInit, OnDestroy, signal } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { FormDataHelper, TranslateHelper } from '@app/_helpers';
import { BannerVendorService, VendorPaymentPlanService, VendorPlanFeatureService, AuthenticationService, CartService } from '@app/_services';
import { SharedService } from '@app/_services/shared.service';
import { SubscriptionService } from '@app/_services/subscription.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BannerVendorComponent } from './vendor-banner/banner-vendor.component';
import { SafeHtmlPipe } from '@app/shared/pipes/safe-html.pipe';
import { takeUntil, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DecimalPipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'payplan',
    templateUrl: './apply-for-vendor.component.html',
    styleUrls: ['./apply-for-vendor.component.scss'],
    providers: [FormDataHelper, TranslateHelper],
    standalone: true,
    imports: [
        TranslateModule,
        NgIf,
        NgForOf,
        NgClass,
        MatDividerModule,
        RouterLink,
        MatIconModule,
        SafeHtmlPipe,
        DecimalPipe
    ]
})
export class ApplyForVendorComponent implements OnInit, OnDestroy {
    isMobile: boolean = this.deviceDetector.isMobile();
    isAnnual = false;
    headerImg = this.isMobile
        ? 'assets/images/extra/backgroundMobile.png'
        : this.translateService.currentLang == 'ar'
        ? 'assets/images/extra/backgroundar.png'
        : 'assets/images/extra/background.png';
    headerTitle = '';
    headerInfo = '';
    whyTransformTitle = '';
    whyJoinTitle = '';
    bannerList: BannerVendorComponent[] = [];
    plans: any[] = [];
    private featureMap = new Map<string, any>();
    filteredPlans: any[] = [];
    extraServices: string[] = [];
    serviceCheckboxes = {};
    selectedCountry: any = null;
    private destroy$ = new Subject<void>();
    
    // Billing type selection for each plan
    selectedBillingType: { [planId: string]: 'monthly' | 'yearly' } = {};
    
    // Track expanded features for each plan
    expandedFeatures: { [planId: string]: boolean } = {};

    /* form = this.fb.group({
        companyName: [null, [Validators.required]],
        contactName: [null, [Validators.required]],
        email: [null, [Validators.required, Validators.email]],
        phone: [null, [Validators.required]],
        crCopy: [null],
        vatCopy: [null],
        logo: [null],
    }) */

    constructor(
        private vendorPaymentPlanService: VendorPaymentPlanService,
        private bannerVendorService: BannerVendorService,
        private sharedService: SharedService,
        private authenticationService: AuthenticationService,
        private subscriptionService: SubscriptionService,
        private toastr: ToastrService,
        private cartService: CartService,
        /* private fb: FormBuilder,
        private vendorRequestService: VendorRequestService,

        private fd: FormDataHelper,
        private http: HttpClient,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef, */
        private router: Router,
        private deviceDetector: DeviceDetectorService,
        public translateService: TranslateService,
        public translateHelper: TranslateHelper,
        private vendorPlanFeatureService: VendorPlanFeatureService
    ) {
        this.translateService
            .get([
                'become_a_vendor_tagline',
                'transform_your_business_tagline',
                'why_transform_into_ecommerce',
                'why_join_qetaat',
                'physical_boundaries_tagline'
            ])
            .subscribe(texts => {
                this.headerTitle = texts.become_a_vendor_tagline;
                this.headerInfo = texts.transform_your_business_tagline;
                this.whyTransformTitle = texts.why_transform_into_ecommerce;
                this.whyJoinTitle = texts.why_join_qetaat;
            });
    }

    signUp(type: string) {
        // Check if user is logged in
        this.authenticationService.currentUser.pipe(
            takeUntil(this.destroy$),
            take(1)
        ).subscribe(currentUser => {
            if (!currentUser || !currentUser._id) {
                // User is not logged in - show message and redirect to register
                this.toastr.info(
                    'Please login or register first to become a vendor',
                    'Authentication Required'
                );
                this.router.navigate(['/register']);
                return;
            }
            
            // User is logged in - proceed to vendor form
            this.router.navigate(['/vendor-form', type]);
        });
    }

    ngOnInit() {
        // Subscribe to current country
        this.sharedService.currentCountry$
            .pipe(takeUntil(this.destroy$))
            .subscribe(country => {
                this.selectedCountry = country;
                this.loadPlans();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadPlans() {
        // First, load active features and build a map for id -> feature
        this.vendorPlanFeatureService.getFeatures({ page: 1, limit: 1000, status: 'A' }).subscribe({
            next: (featuresRes) => {
                if (featuresRes?.code === 200) {
                    const features = featuresRes.data || [];
                    this.featureMap = new Map(features.map((f: any) => [f._id, f]));
                } else {
                    this.featureMap = new Map();
                }

                // Then load plans and enrich with populated features if missing
                this.vendorPaymentPlanService.getAllPlans().subscribe((result: any) => {
                    if (result.code == 200) {
                        this.plans = (result.data || []).map((plan: any) => {
                            if (Array.isArray(plan.subscription_features_populated) && plan.subscription_features_populated.length > 0) {
                                return plan; // already populated by backend
                            }
                            const ids: string[] = Array.isArray(plan.subscription_features) ? plan.subscription_features : [];
                            const populated = ids.map((id: string) => this.featureMap.get(id)).filter(Boolean);
                            return { ...plan, subscription_features_populated: populated };
                        });
                        this.filterPlansByCountry();
                    }
                });
            },
            error: () => {
                // Fallback: still load plans even if features call fails
                this.vendorPaymentPlanService.getAllPlans().subscribe((result: any) => {
                    if (result.code == 200) {
                        this.plans = result.data || [];
                        this.filterPlansByCountry();
                    }
                });
            }
        });
    }

    filterPlansByCountry() {
        if (!this.selectedCountry || !this.plans) {
            this.filteredPlans = [];
            return;
        }

        // Filter plans by country and only show active plans (excluding default plans)
        this.filteredPlans = this.plans.filter(plan => {
            // Exclude default plans - they are auto-assigned and not for purchase
            if (plan.isDefault === true) return false;
            
            // Check if plan has country and price
            if (!plan.country) return false;
            const isActive = plan.status === 'A';
            const hasPrice = plan.monthly_price || plan.yearly_price;
            if (!isActive || !hasPrice) return false;

            // Get the country name from the plan (either English or Arabic)
            const planCountryEn = (plan.country.en || '').toLowerCase().trim();
            const planCountryAr = (plan.country.ar || '').toLowerCase().trim();
            
            // Get selected country details
            const selectedCountryNameEn = (this.selectedCountry.name?.en || '').toLowerCase().trim();
            const selectedCountryNameAr = (this.selectedCountry.name?.ar || '').toLowerCase().trim();
            const selectedCountryCode = (this.selectedCountry.code || '').toLowerCase().trim();
            
            // Match by:
            // 1. Exact country name match (English or Arabic)
            // 2. Plan country matches selected country code (e.g., plan has "KSA" and selector has code "SA")
            // 3. Plan country contains or equals country code variations
            const exactMatch = 
                planCountryEn === selectedCountryNameEn || 
                planCountryAr === selectedCountryNameAr;
            
            const codeMatch = 
                planCountryEn === selectedCountryCode ||
                planCountryEn.includes(selectedCountryCode) ||
                selectedCountryNameEn.includes(planCountryEn);
            
            // Handle common abbreviations
            const abbreviationMatch = this.matchCountryAbbreviation(planCountryEn, selectedCountryNameEn, selectedCountryCode);
            
            return exactMatch || codeMatch || abbreviationMatch;
        });
        
        // Initialize billing types for filtered plans
        this.initializeBillingTypes();
    }

    // Initialize billing types for all plans - set default to available option
    initializeBillingTypes() {
        this.filteredPlans.forEach(plan => {
            if (!this.selectedBillingType[plan._id]) {
                this.selectedBillingType[plan._id] = this.getDefaultBillingType(plan);
            }
        });
    }

    // Set billing type for a specific plan
    setBillingType(planId: string, type: 'monthly' | 'yearly') {
        this.selectedBillingType[planId] = type;
    }

    // Get the default billing type based on what's available
    getDefaultBillingType(plan: any): 'monthly' | 'yearly' {
        if (plan.monthly_price && plan.yearly_price) {
            // Both available, default to monthly
            return 'monthly';
        } else if (plan.monthly_price) {
            return 'monthly';
        } else if (plan.yearly_price) {
            return 'yearly';
        }
        return 'monthly'; // fallback
    }

    // Get the display price based on selected billing type
    getDisplayPrice(plan: any): number {
        const billingType = this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
        return billingType === 'monthly' ? plan.monthly_price : plan.yearly_price;
    }

    // Get the current billing type for a plan (with fallback)
    getCurrentBillingType(plan: any): 'monthly' | 'yearly' {
        return this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
    }

    // Check if current billing type has active discount
    hasActiveDiscount(plan: any): boolean {
        const billingType = this.getCurrentBillingType(plan);
        if (billingType === 'monthly') {
            return this.isMonthlyDiscountActive(plan);
        } else {
            return this.isYearlyDiscountActive(plan);
        }
    }

    // Get current discount rate
    getCurrentDiscountRate(plan: any): number {
        const billingType = this.getCurrentBillingType(plan);
        return billingType === 'monthly' ? plan.monthly_discount_rate : plan.yearly_discount_rate;
    }

    // Toggle features expansion for a plan
    toggleFeatures(planId: string) {
        this.expandedFeatures[planId] = !this.expandedFeatures[planId];
    }

    // Subscribe to plan method
    subscribeToplan(plan: any) {
        // Get the currently selected billing type for this plan
        const billingType = this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
      
        
        // Check authentication status by getting current user from AuthenticationService
        this.authenticationService.currentUser.pipe(
            takeUntil(this.destroy$),
            take(1) // Only take the first emission to avoid multiple subscriptions
        ).subscribe(currentUser => {
            // Check if user is not logged in
            if (!currentUser || !currentUser._id) {
                this.toastr.error(
                    'You need to login or register first',
                    'Authentication Required'
                );
                return;
            }

            // Check if user is a customer (not a vendor)
            if (!currentUser.isVendor) {
                // Redirect to vendor form for customers
                this.router.navigate(['/vendor-form', 'register'], {
                    queryParams: { 
                        planId: plan._id,
                        billingType: billingType
                    }
                });
                return;
            }

            // User is a Vendor Admin - add subscription to cart and proceed to checkout
            this.addSubscriptionToCart(plan, billingType);
        });
    }

    // Add subscription to cart
    private addSubscriptionToCart(plan: any, billingType: 'monthly' | 'yearly') {
        // First, check if user has an active subscription
        this.authenticationService.currentUser.pipe(take(1)).subscribe(user => {
            if (!user?._id) {
                this.toastr.error('Please login to purchase a subscription plan');
                return;
            }

            // Check if user already has an active subscription
            this.subscriptionService.checkUserSubscription(user._id).subscribe(response => {
                if (response.code === 200) {
                    const data = response.data;
                    
                    // If user has active subscription and it's NOT a default plan, block purchase
                    if (data.hasActiveSubscription && !data.canPurchaseNewPlan) {
                        this.toastr.error('You already have an active subscription plan. Please manage your existing subscription before purchasing a new one.');
                        return;
                    }

                    // User can proceed - either no subscription or has default plan that will be replaced
                    this.proceedToCheckout(plan, billingType);
                } else {
                    this.toastr.error('Error checking subscription status. Please try again.');
                }
            }, error => {
                console.error('Error checking subscription:', error);
                this.toastr.error('Error checking subscription status. Please try again.');
            });
        });
    }

    // Proceed to checkout after validation
    private proceedToCheckout(plan: any, billingType: 'monthly' | 'yearly') {
        // Get the actual price based on selected billing type
        const priceField = billingType === 'monthly' ? 'monthly_price' : 'yearly_price';
        const selectedPrice = plan[priceField];
        
    
        // Calculate discount if applicable
        const discountRateField = billingType === 'monthly' ? 'monthly_discount_rate' : 'yearly_discount_rate';
        const discountStartsField = billingType === 'monthly' ? 'monthly_discount_starts' : 'yearly_discount_starts';
        const discountEndsField = billingType === 'monthly' ? 'monthly_discount_ends' : 'yearly_discount_ends';
        
        let discountRate = 0;
        let originalPrice = selectedPrice;
        let finalPrice = selectedPrice;
        
        // Check if discount is active
        if (plan[discountRateField] && plan[discountRateField] > 0) {
            const now = new Date();
            const discountStartDate = plan[discountStartsField] ? new Date(plan[discountStartsField]) : null;
            const discountEndDate = plan[discountEndsField] ? new Date(plan[discountEndsField]) : null;
            
            const isDiscountActive = (!discountStartDate || now >= discountStartDate) && 
                                    (!discountEndDate || now <= discountEndDate);
            
            if (isDiscountActive) {
                discountRate = plan[discountRateField];
                const discountAmount = originalPrice * (discountRate / 100);
                finalPrice = originalPrice - discountAmount;
            }
        }

        // Navigate to checkout-s route with subscription details
        // Using checkout-s to avoid the CheckoutGuard that requires cart items
        // Route format: /checkout-s/:id/:type where id=planId and type=vendor-subscription
        // st parameter must match the ESubscriptionPaymentType enum: 'monthly' or 'annualy'
        const subscriptionType = billingType === 'monthly' ? 'monthly' : 'annualy';
        
        this.router.navigate(['/checkout-s', plan._id, 'vendor-subscription'], {
            queryParams: {
                st: subscriptionType, // This should be 'monthly' or 'annualy' to match ESubscriptionPaymentType
                billingType: billingType,
                planName: this.translateService.currentLang === 'ar' ? (plan.name?.ar || plan.name?.en) : (plan.name?.en || plan.name?.ar),
                price: finalPrice,
                originalPrice: originalPrice,
                discountRate: discountRate,
                duration: billingType === 'yearly' ? 12 : 1
            }
        });
    }

    // Helper method to match country abbreviations and variations
    private matchCountryAbbreviation(planCountry: string, selectedCountry: string, countryCode: string): boolean {
        // Common country abbreviations and variations
        const countryMappings: { [key: string]: string[] } = {
            'saudi arabia': ['ksa', 'sa', 'saudi'],
            'united arab emirates': ['uae', 'emirates'],
            'bahrain': ['bhr', 'bh'],
            'kuwait': ['kw', 'kwt'],
            'oman': ['om', 'omn'],
            'qatar': ['qa', 'qat'],
            'egypt': ['eg', 'egy'],
            'jordan': ['jo', 'jor'],
            'lebanon': ['lb', 'lbn'],
            'iraq': ['iq', 'irq']
        };

        // Check if plan country matches any abbreviation of the selected country
        const selectedAbbreviations = countryMappings[selectedCountry] || [];
        if (selectedAbbreviations.includes(planCountry)) {
            return true;
        }

        // Check if selected country matches any abbreviation of the plan country
        const planAbbreviations = countryMappings[planCountry] || [];
        if (planAbbreviations.includes(selectedCountry) || planAbbreviations.includes(countryCode)) {
            return true;
        }

        return false;
    }

    // Get card color based on index
    getCardColor(index: number): any {
        const colors = [
            {
                bg: 'from-blue-500 to-indigo-600',
                text: 'text-white',
                priceText: 'text-white',
                oldPriceText: 'text-blue-200',
                badge: 'bg-red-500',
                iconBg: 'bg-white/20',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-blue-600',
                buttonHover: 'hover:bg-blue-50',
                tooltipBg: 'bg-white border border-blue-200',
                tooltipTitle: 'text-blue-900',
                tooltipText: 'text-blue-700'
            },
            {
                bg: 'from-purple-500 to-pink-600',
                text: 'text-white',
                priceText: 'text-white',
                oldPriceText: 'text-purple-200',
                badge: 'bg-red-500',
                iconBg: 'bg-white/20',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-purple-600',
                buttonHover: 'hover:bg-purple-50',
                tooltipBg: 'bg-white border border-purple-200',
                tooltipTitle: 'text-purple-900',
                tooltipText: 'text-purple-700'
            },
            {
                bg: 'from-cyan-500 to-teal-600',
                text: 'text-white',
                priceText: 'text-white',
                oldPriceText: 'text-cyan-200',
                badge: 'bg-red-500',
                iconBg: 'bg-white/20',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-cyan-600',
                buttonHover: 'hover:bg-cyan-50',
                tooltipBg: 'bg-white border border-cyan-200',
                tooltipTitle: 'text-cyan-900',
                tooltipText: 'text-cyan-700'
            },
            {
                bg: 'from-rose-500 to-orange-600',
                text: 'text-white',
                priceText: 'text-white',
                oldPriceText: 'text-rose-200',
                badge: 'bg-red-500',
                iconBg: 'bg-white/20',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-rose-600',
                buttonHover: 'hover:bg-rose-50',
                tooltipBg: 'bg-white border border-rose-200',
                tooltipTitle: 'text-rose-900',
                tooltipText: 'text-rose-700'
            },
            {
                bg: 'from-violet-500 to-indigo-600',
                text: 'text-white',
                priceText: 'text-white',
                oldPriceText: 'text-violet-200',
                badge: 'bg-red-500',
                iconBg: 'bg-white/20',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-violet-600',
                buttonHover: 'hover:bg-violet-50',
                tooltipBg: 'bg-white border border-violet-200',
                tooltipTitle: 'text-violet-900',
                tooltipText: 'text-violet-700'
            },
            {
                bg: 'from-emerald-500 to-green-600',
                text: 'text-white',
                priceText: 'text-white',
                oldPriceText: 'text-emerald-200',
                badge: 'bg-red-500',
                iconBg: 'bg-white/20',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-emerald-600',
                buttonHover: 'hover:bg-emerald-50',
                tooltipBg: 'bg-white border border-emerald-200',
                tooltipTitle: 'text-emerald-900',
                tooltipText: 'text-emerald-700'
            }
        ];
        
        return colors[index % colors.length];
    }

    // Check if monthly discount is currently active
    isMonthlyDiscountActive(plan: any): boolean {
        if (!plan.monthly_discount_rate || plan.monthly_discount_rate <= 0) {
            return false;
        }
        
        const now = new Date();
        const startDate = plan.monthly_discount_starts ? new Date(plan.monthly_discount_starts) : null;
        const endDate = plan.monthly_discount_ends ? new Date(plan.monthly_discount_ends) : null;
        
        if (startDate && now < startDate) {
            return false;
        }
        if (endDate && now > endDate) {
            return false;
        }
        
        return true;
    }

    // Check if yearly discount is currently active
    isYearlyDiscountActive(plan: any): boolean {
        if (!plan.yearly_discount_rate || plan.yearly_discount_rate <= 0) {
            return false;
        }
        
        const now = new Date();
        const startDate = plan.yearly_discount_starts ? new Date(plan.yearly_discount_starts) : null;
        const endDate = plan.yearly_discount_ends ? new Date(plan.yearly_discount_ends) : null;
        
        if (startDate && now < startDate) {
            return false;
        }
        if (endDate && now > endDate) {
            return false;
        }
        
        return true;
    }

    // Get count of active features
    getActiveFeaturesCount(plan: any): number {
        if (!plan.features || !Array.isArray(plan.features)) {
            return 0;
        }
        return plan.features.filter((feature: any) => feature.active === true).length;
    }

    // Calculate final price with discount only (no tax)
    calculateFinalPrice(price: number, plan: any): number {
        if (!price) {
            return 0;
        }
        
        let finalPrice = price;
        const billingType = this.selectedBillingType[plan._id];
        
        // Apply appropriate discount based on billing type
        if (billingType === 'monthly' && this.isMonthlyDiscountActive(plan)) {
            finalPrice = price - (price * (plan.monthly_discount_rate / 100));
        } else if (billingType === 'yearly' && this.isYearlyDiscountActive(plan)) {
            finalPrice = price - (price * (plan.yearly_discount_rate / 100));
        }
        
        return Math.round(finalPrice * 100) / 100; // Round to 2 decimal places
    }

    isBoolean(value): boolean {
        return typeof value === 'boolean';
    }

    scrollTo(el: HTMLElement) {
        el.scrollIntoView();
    }

    heroImage = signal('assets/images/banners/ImageBanner.png');
    openFaqIndex = signal<number | null>(null);

    cTextt = this.translateService.translations[
        this.translateService.currentLang
    ];
    cText = computed(() => this.textData[this.translateService.currentLang]);

    toggleFaq(index: number) {
        this.openFaqIndex.update(currentIndex =>
            currentIndex === index ? null : index
        );
    }
    private textData = {
        en: {
            hero: {
                title:
                    "Become a Partner in Revolutionizing the Gulf's Construction Industry",
                subtitle:
                    'One project at a time. Connecting contractors, suppliers, and developers on a unified platform.',
                cta: 'Become a Vendor Today'
            },
            whyTransform: {
                title: 'Transform Your Business with E-commerce',
                subtitle:
                    'Step into the future of the digitized construction value chain.',
                features: [
                    {
                        title: 'Stay Ahead of Trends',
                        description:
                            'Capitalize on mobile penetration and the shift to online business.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>'
                    },
                    {
                        title: 'Expand Your Reach',
                        description:
                            'Operate 24/7 and connect with customers beyond physical boundaries.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
                    },
                    {
                        title: 'Boost Efficiency',
                        description:
                            'Cut operational costs and embrace sustainability with a paperless workflow.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>'
                    },
                    {
                        title: 'Optimize Operations',
                        description:
                            'Digitize sales, marketing, and logistics with flexible payment solutions.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>'
                    },
                    {
                        title: 'Unlock Growth',
                        description:
                            'Discover new markets and expand your business opportunities.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>'
                    }
                ]
            },
            coreFeatures: {
                title: 'A Platform Built for Your Success',
                features: [
                    {
                        title: 'Connect with Industry Leaders',
                        description:
                            'Instantly access a verified network of top-tier construction companies, suppliers, and contractors.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1m-1-4h1m-1 4h1" /></svg>'
                    },
                    {
                        title: 'Streamlined Project Management',
                        description:
                            'Simplify your workflow by managing quotes, orders, and communications in one unified hub.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.28-1.25-1.44-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.28-1.25 1.44-1.857m0 0A5.002 5.002 0 0112 15a5.002 5.002 0 013.56 1.143M12 12a3 3 0 100-6 3 3 0 000 6z" /></svg>'
                    },
                    {
                        title: 'Secure & Compliant',
                        description:
                            'Transact with confidence through VAT-compliant invoicing, secure payments, and robust data protection.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 20.944a11.955 11.955 0 019-4.944 11.955 11.955 0 019 4.944 12.02 12.02 0 00-3.382-8.944z" /></svg>'
                    },
                    {
                        title: 'Boost Your Business',
                        description:
                            'Elevate your market presence, uncover new leads, and expand your customer base.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>'
                    }
                ]
            },
            howItWorks: {
                title: 'How It Works',
                subtitle:
                    'Joining our platform is simple and straightforward. Follow these three easy steps to get started.',
                steps: [
                    {
                        title: 'Register Your Company',
                        description:
                            'Complete our multi-step registration form with your business details.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>'
                    },
                    {
                        title: 'Choose Your Plan',
                        description:
                            'Select a subscription plan that fits your business size and needs.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>'
                    },
                    {
                        title: 'Access New Markets',
                        description:
                            'Start connecting with projects and partners across the Gulf region.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2h8a2 2 0 002-2v-1a2 2 0 012-2h1.945M7.884 5.06l.546-1.638A2 2 0 0110.385 2h3.23a2 2 0 011.955 1.422l.546 1.638m-9.288 0H18.41m-5.656 12.022L12 22l-1.121-1.422M6 11.945V19a2 2 0 002 2h8a2 2 0 002-2v-7.055-2.262" /></svg>'
                    }
                ]
            },
            faq: {
                title: 'Frequently Asked Questions',
                subtitle:
                    "Have questions? We've got answers. If you can't find what you're looking for, feel free to contact us.",
                questions: [
                    {
                        q: 'What types of companies can join Qetaat?',
                        a:
                            'We welcome all businesses in the construction value chain, including general contractors, subcontractors, material suppliers, consultants, engineers, and property developers.'
                    },
                    {
                        q: 'Is there a free trial available?',
                        a:
                            "Yes, we do offer a free trial for some services that gives you full access to those services' features. No credit card is required to get started."
                    },
                    {
                        q: 'What are the subscription costs after the trial?',
                        a:
                            "We offer a range of plans to suit different business sizes, from our Basic plan for startups to our Enterprise solution for large organizations. You can find detailed pricing in the 'Plan Selection' step of our registration."
                    },
                    {
                        q:
                            'How does Qetaat ensure the quality of vendors on the platform?',
                        a:
                            'All vendors go through a verification process where we check their business registration, licenses, and track record to maintain a high-quality, trusted network.'
                    },
                    {
                        q:
                            'Can I use the platform for projects outside of the Gulf region?',
                        a:
                            'Currently, our primary focus is on the GCC market, starting with Bahrain and expanding across the region. We are constantly evaluating opportunities for future expansion.'
                    }
                ]
            },
            gulfExpansion: {
                title: 'Gulf Expansion Roadmap',
                checkIcon:
                    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>',
                arrowIcon:
                    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>',
                bahrain: {
                    icon:
                        '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
                    title: 'Now Live in Bahrain',
                    status: 'Live',
                    description:
                        'Successfully serving the Bahraini construction market with local partnerships and cultural understanding.',
                    stats: [
                        { value: '100+', label: 'Local Partners' },
                        { value: '12,000+', label: 'Products' },
                        { value: '3M+', label: 'Visits' }
                    ],
                    features: [
                        'Local business license and compliance',
                        'Arabic-speaking customer support',
                        'Regional material suppliers',
                        'Multiple payment and delivery options'
                    ]
                },
                saudi: {
                    icon:
                        '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>',
                    title: 'Next: Saudi Arabia',
                    status: 'Launching Early 2026',
                    description:
                        'Bringing our proven platform to the largest construction market in the Gulf.',
                    opportunitiesTitle: 'Market Opportunities:',
                    opportunities: [
                        'GIG and Vision 2030 projects',
                        'Smart city developments',
                        'Sustainable construction focus',
                        'Massive infrastructure investments'
                    ],
                    promoIcon:
                        '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1m-1-4h1m-1 4h1" /></svg>',
                    promoText:
                        'Preparing for a transformative leap in the Gulf construction industry'
                },
                roadmap: [
                    {
                        country: 'Bahrain',
                        date: 'Operational since 2023',
                        icon: '<span class="text-green-500">&#10003;</span>'
                    },
                    {
                        country: 'Saudi Arabia',
                        date: 'Launching Early 2026',
                        icon: '<span class="text-blue-500">&rarr;</span>'
                    },
                    {
                        country: 'GCC Expansion',
                        date: '2026',
                        icon: '<span>&#9202;</span>'
                    },
                    {
                        country: 'Regional',
                        date: '2027',
                        icon: '<span>&#9202;</span>'
                    }
                ]
            },
            cta: {
                title: 'Be Part of Our Gulf Expansion',
                subtitle: 'Join early and get exclusive access to new markets.',
                button: 'Join Expansion List'
            }
        },
        ar: {
            hero: {
                title:
                    'كن شريكنا في إحداث ثورة تقنية في صناعة الإنشائات الخليجية',
                subtitle:
                    'مشروع تلو الآخر. نربط المقاولين والموردين والمطورين على منصة موحدة.',
                cta: 'كن بائعًا اليوم'
            },
            whyTransform: {
                title: 'حوّل أعمالك مع التجارة الإلكترونية',
                subtitle:
                    'ادخل إلى مستقبل سلسلة القيمة الرقمية في قطاع الإنشاءات.',
                features: [
                    {
                        title: 'واكب أحدث التوجهات',
                        description:
                            'استفد من الانتشار الواسع للجوال والتحول الرقمي للأعمال.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>'
                    },
                    {
                        title: 'وسّع نطاق وصولك',
                        description:
                            'اعمل على مدار الساعة وتواصل مع العملاء بلا حدود جغرافية.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
                    },
                    {
                        title: 'عزز كفاءتك',
                        description:
                            'قلل التكاليف التشغيلية وتبنّ الاستدامة بسير عمل خالٍ من الورق.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>'
                    },
                    {
                        title: 'حسّن عملياتك',
                        description:
                            'أتمتة المبيعات والتسويق والخدمات اللوجستية مع حلول دفع مرنة.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>'
                    },
                    {
                        title: 'أطلق العنان للنمو',
                        description:
                            'اكتشف أسواقًا جديدة ووسّع فرص أعمالك التجارية.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>'
                    }
                ]
            },
            coreFeatures: {
                title: 'منصة مصممة لنجاحك',
                features: [
                    {
                        title: 'تواصل مع رواد القطاع',
                        description:
                            'وصول فوري إلى شبكة موثوقة من نخبة شركات الإنشاءات والموردين والمقاولين.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1m-1-4h1m-1 4h1" /></svg>'
                    },
                    {
                        title: 'إدارة مبسطة للمشاريع',
                        description:
                            'بسّط سير عملك عبر إدارة عروض الأسعار والطلبات والمراسلات في منصة مركزية واحدة.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.28-1.25-1.44-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.28-1.25 1.44-1.857m0 0A5.002 5.002 0 0112 15a5.002 5.002 0 013.56 1.143M12 12a3 3 0 100-6 3 3 0 000 6z" /></svg>'
                    },
                    {
                        title: 'آمن ومتوافق',
                        description:
                            'أجرِ معاملاتك بثقة من خلال فواتير متوافقة مع ضريبة القيمة المضافة، ومدفوعات آمنة، وحماية قوية للبيانات.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 20.944a11.955 11.955 0 019-4.944 11.955 11.955 0 019 4.944 12.02 12.02 0 00-3.382-8.944z" /></svg>'
                    },
                    {
                        title: 'عزز أعمالك',
                        description:
                            'عزز حضورك في السوق، واكتشف فرصًا جديدة، ووسّع قاعدة عملائك.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>'
                    }
                ]
            },
            howItWorks: {
                title: 'كيف نعمل',
                subtitle:
                    'الانضمام إلى منصتنا بسيط ومباشر. اتبع هذه الخطوات الثلاث السهلة للبدء.',
                steps: [
                    {
                        title: 'سجل شركتك',
                        description:
                            'أكمل نموذج التسجيل متعدد الخطوات الخاص بنا بتفاصيل عملك.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>'
                    },
                    {
                        title: 'اختر خطتك',
                        description:
                            'حدد خطة اشتراك تناسب حجم عملك واحتياجاته.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>'
                    },
                    {
                        title: 'الوصول إلى أسواق جديدة',
                        description:
                            'ابدأ بالتواصل مع المشاريع والشركاء في جميع أنحاء منطقة الخليج.',
                        icon:
                            '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2h8a2 2 0 002-2v-1a2 2 0 012-2h1.945M7.884 5.06l.546-1.638A2 2 0 0110.385 2h3.23a2 2 0 011.955 1.422l.546 1.638m-9.288 0H18.41m-5.656 12.022L12 22l-1.121-1.422M6 11.945V19a2 2 0 002 2h8a2 2 0 002-2v-7.055-2.262" /></svg>'
                    }
                ]
            },
            faq: {
                title: 'الأسئلة الشائعة',
                subtitle:
                    'لديك أسئلة؟ لدينا الإجابات. إذا لم تجد ما تبحث عنه، فلا تتردد في الاتصال بنا.',
                questions: [
                    {
                        q: 'ما أنواع الشركات التي يمكنها الانضمام إلى قطاعات؟',
                        a:
                            'نرحب بجميع الشركات في سلسلة القيمة لقطاع البناء، بما في ذلك المقاولون العامون، والمقاولون الفرعيون، وموردو المواد، والاستشاريون، والمهندسون، والمطورون العقاريون.'
                    },
                    {
                        q: 'هل تتوفر فترة تجريبية مجانية؟',
                        a:
                            'نعم، نقدم فترة تجريبية مجانية لبعض الخدمات تمنحك وصولاً كاملاً إلى ميزات تلك الخدمات. لا يلزم وجود بطاقة ائتمان للبدء.'
                    },
                    {
                        q: 'ما هي تكاليف الاشتراك بعد الفترة التجريبية؟',
                        a:
                            "نقدم مجموعة من الخطط لتناسب مختلف أحجام الأعمال، من خطتنا الأساسية للشركات الناشئة إلى حلول المؤسسات للمنظمات الكبيرة. يمكنك العثور على الأسعار المفصلة في خطوة 'اختيار الخطة' في تسجيلنا."
                    },
                    {
                        q: 'كيف تضمن قطاعات جودة البائعين على المنصة؟',
                        a:
                            'يخضع جميع البائعين لعملية تحقق حيث نتحقق من تسجيلهم التجاري وتراخيصهم وسجلهم الحافل للحفاظ على شبكة موثوقة وعالية الجودة.'
                    },
                    {
                        q:
                            'هل يمكنني استخدام المنصة لمشاريع خارج منطقة الخليج؟',
                        a:
                            'حاليًا، ينصب تركيزنا الأساسي على سوق دول مجلس التعاون الخليجي، بدءًا من البحرين والتوسع في جميع أنحاء المنطقة. نحن نقيم باستمرار فرص التوسع في المستقبل.'
                    }
                ]
            },
            gulfExpansion: {
                title: 'خارطة طريق التوسع الخليجي',
                checkIcon:
                    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>',
                arrowIcon:
                    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>',
                bahrain: {
                    icon:
                        '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
                    title: 'الآن في البحرين',
                    status: 'مباشر',
                    description:
                        'نخدم بنجاح سوق الإنشاءات البحريني بشراكات محلية وفهم ثقافي.',
                    stats: [
                        { value: '+100', label: 'شريك محلي' },
                        { value: '+12,000', label: 'منتج' },
                        { value: '+3 مليون', label: 'زيارة' }
                    ],
                    features: [
                        'الامتثال للرخص التجارية المحلية',
                        'دعم عملاء ناطقين بالعربية',
                        'موردون محليون للمواد',
                        'خيارات متعددة للدفع و التوصيل'
                    ]
                },
                saudi: {
                    icon:
                        '<svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>',
                    title: 'التالي: السعودية',
                    status: 'الإطلاق في اوائل 2026',
                    description:
                        'نقدم منصتنا المثبتة إلى أكبر سوق للإنشاءات في الخليج.',
                    opportunitiesTitle: 'فرص السوق:',
                    opportunities: [
                        'مشاريع GIG ورؤية 2030',
                        'تطوير المدن الذكية',
                        'التركيز على البناء المستدام',
                        'استثمارات ضخمة في البنية التحتية'
                    ],
                    promoIcon:
                        '<svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1m-1-4h1m-1 4h1" /></svg>',
                    promoText: 'الاستعداد لنقلة نوعية لصناعة الانشائات الخليجية'
                },
                roadmap: [
                    {
                        country: 'البحرين',
                        date: 'عاملة منذ 2023',
                        icon: '<span class="text-green-500">&#10003;</span>'
                    },
                    {
                        country: 'السعودية',
                        date: 'الإطلاق في اوائل 2026',
                        icon: '<span class="text-blue-500">&larr;</span>'
                    },
                    {
                        country: 'دول الخليج',
                        date: '2026',
                        icon: '<span>&#9202;</span>'
                    },
                    {
                        country: 'المنطقة',
                        date: '2027',
                        icon: '<span>&#9202;</span>'
                    }
                ]
            },
            cta: {
                title: 'كن جزءًا من توسعنا الخليجي',
                subtitle: 'انضم مبكرًا واحصل على وصول حصري إلى أسواق جديدة.',
                button: 'انضم إلى قائمة التوسع'
            }
        }
    };

    /* submit() {
        this.vendorRequestService.setSelectedPlan({ planId: this.plan._id as string, services: this.extraServices });
        this.router.navigate(['/vendor-form']);
    }

    isSelected(serviceId) {
        return this.extraServices.includes(serviceId);
    }

    toggleService(service: any) {
        this.setService(service, !this.isSelected(service._id));
    }

    setService(service: any, state: boolean) {
        if (state) {
            if (service.group && service.group?.length > 0) {
                let sameGroupIds = _.map(_.filter(this.plan.services || [], (serv) => { return serv.group == service.group }), '_id');
                if (sameGroupIds.length > 0) _.remove(this.extraServices, (serviceId) => { return sameGroupIds.includes(serviceId) });
            }
            this.extraServices.push(service._id);
            this.extraServices = _.uniq(this.extraServices);
        } else {
            _.remove(this.extraServices, (serviceId) => { return serviceId == service._id; });
        }
        this.cdr.detectChanges();
    }

    updateServices(event, service) {
        this.setService(service, event.checked);
    }
    */
}
